/**
 * ZK Employee Mapping Component
 *
 * Maps ZK device user IDs to ERP employees.
 */

import React, { useState, useEffect, useCallback } from 'react';
import {
  Table,
  Button,
  Space,
  Select,
  Tag,
  message,
  Alert,
  Typography,
  Row,
  Col,
  Card,
  Statistic,
  Empty,
  Tooltip
} from 'antd';
import {
  SyncOutlined,
  LinkOutlined,
  UserOutlined,
  CheckCircleOutlined,
  WarningOutlined,
  ReloadOutlined
} from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import { zkDeviceApi, zkMappingApi, ZkDevice, ZkEmployeeMapping, ZkUser } from '../../services/zkService';
import { employeeService, Employee } from '../../../../services/hrService';

const { Text } = Typography;
const { Option } = Select;

const ZKEmployeeMappingComponent: React.FC = () => {
  const [devices, setDevices] = useState<ZkDevice[]>([]);
  const [selectedDevice, setSelectedDevice] = useState<string | null>(null);
  const [mappings, setMappings] = useState<ZkEmployeeMapping[]>([]);
  const [employees, setEmployees] = useState<Employee[]>([]);
  const [loading, setLoading] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [autoMapping, setAutoMapping] = useState(false);
  const [pendingChanges, setPendingChanges] = useState<Map<string, string | null>>(new Map());

  // Fetch devices
  useEffect(() => {
    const fetchDevices = async () => {
      try {
        const data = await zkDeviceApi.getAll(true);
        setDevices(data);
        if (data.length > 0 && !selectedDevice) {
          setSelectedDevice(data[0].id);
        }
      } catch (error) {
        message.error('Failed to load devices');
      }
    };
    fetchDevices();
  }, []);

  // Fetch employees
  useEffect(() => {
    const fetchEmployees = async () => {
      try {
        const data = await employeeService.getAll();
        setEmployees(data);
      } catch (error) {
        console.error('Failed to load employees:', error);
      }
    };
    fetchEmployees();
  }, []);

  // Fetch mappings when device changes
  const fetchMappings = useCallback(async () => {
    if (!selectedDevice) return;

    setLoading(true);
    try {
      const data = await zkMappingApi.getAll(selectedDevice);
      setMappings(data);
      setPendingChanges(new Map());
    } catch (error) {
      message.error('Failed to load mappings');
    } finally {
      setLoading(false);
    }
  }, [selectedDevice]);

  useEffect(() => {
    fetchMappings();
  }, [fetchMappings]);

  // Sync users from device
  const handleSyncUsers = async () => {
    if (!selectedDevice) return;

    setSyncing(true);
    try {
      const result = await zkDeviceApi.syncUsers(selectedDevice);
      message.success(`Synced ${result.users.length} users (${result.syncResult.created} new)`);
      fetchMappings();
    } catch (error: any) {
      message.error(error.response?.data?.message || 'Failed to sync users from device');
    } finally {
      setSyncing(false);
    }
  };

  // Auto-map employees
  const handleAutoMap = async () => {
    if (!selectedDevice) return;

    setAutoMapping(true);
    try {
      const result = await zkMappingApi.autoMap(selectedDevice);
      message.success(`Auto-mapped ${result.mapped} employees, ${result.unmapped} remain unmapped`);
      fetchMappings();
    } catch (error: any) {
      message.error(error.response?.data?.message || 'Failed to auto-map employees');
    } finally {
      setAutoMapping(false);
    }
  };

  // Handle mapping change (temporary)
  const handleMappingChange = (mappingId: string, employeeId: string | null) => {
    const newPending = new Map(pendingChanges);
    newPending.set(mappingId, employeeId);
    setPendingChanges(newPending);
  };

  // Save pending changes
  const handleSaveChanges = async () => {
    if (pendingChanges.size === 0) return;

    setLoading(true);
    try {
      const promises = Array.from(pendingChanges.entries()).map(([mappingId, employeeId]) =>
        zkMappingApi.update(mappingId, { employeeId })
      );

      await Promise.all(promises);
      message.success(`Saved ${pendingChanges.size} mapping(s)`);
      fetchMappings();
    } catch (error) {
      message.error('Failed to save mappings');
    } finally {
      setLoading(false);
    }
  };

  // Get current value (pending or saved)
  const getCurrentEmployeeId = (mapping: ZkEmployeeMapping): string | null => {
    if (pendingChanges.has(mapping.id)) {
      return pendingChanges.get(mapping.id) || null;
    }
    return mapping.employeeId;
  };

  // Table columns
  const columns: ColumnsType<ZkEmployeeMapping> = [
    {
      title: 'ZK User ID',
      dataIndex: 'zkUserId',
      key: 'zkUserId',
      width: 100,
      render: (id) => <Text code>{id}</Text>
    },
    {
      title: 'ZK User Name',
      dataIndex: 'zkUserName',
      key: 'zkUserName',
      render: (name) => name || <Text type="secondary">-</Text>
    },
    {
      title: '',
      key: 'arrow',
      width: 50,
      render: () => <LinkOutlined style={{ color: '#1890ff' }} />
    },
    {
      title: 'ERP Employee',
      key: 'employee',
      width: 300,
      render: (_, record) => {
        const currentValue = getCurrentEmployeeId(record);
        const hasChange = pendingChanges.has(record.id);

        return (
          <Select
            style={{ width: '100%' }}
            value={currentValue}
            onChange={(value) => handleMappingChange(record.id, value)}
            placeholder="Select employee"
            allowClear
            showSearch
            optionFilterProp="children"
            status={hasChange ? 'warning' : undefined}
          >
            {employees.map(emp => (
              <Option key={emp.id} value={emp.id}>
                {emp.employeeId} - {emp.firstName} {emp.lastName}
              </Option>
            ))}
          </Select>
        );
      }
    },
    {
      title: 'Status',
      key: 'status',
      width: 120,
      render: (_, record) => {
        const currentValue = getCurrentEmployeeId(record);
        const hasChange = pendingChanges.has(record.id);

        if (hasChange) {
          return <Tag color="orange">Unsaved</Tag>;
        }

        return currentValue ? (
          <Tag icon={<CheckCircleOutlined />} color="success">Mapped</Tag>
        ) : (
          <Tag icon={<WarningOutlined />} color="warning">Not Mapped</Tag>
        );
      }
    }
  ];

  // Statistics
  const mappedCount = mappings.filter(m => m.employeeId).length;
  const unmappedCount = mappings.length - mappedCount;

  return (
    <div>
      {/* Device Selector */}
      <Row gutter={16} style={{ marginBottom: 16 }}>
        <Col span={8}>
          <Text strong>Select Device: </Text>
          <Select
            style={{ width: 200, marginLeft: 8 }}
            value={selectedDevice}
            onChange={setSelectedDevice}
            placeholder="Select a device"
          >
            {devices.map(device => (
              <Option key={device.id} value={device.id}>
                {device.deviceName}
              </Option>
            ))}
          </Select>
        </Col>
        <Col span={16} style={{ textAlign: 'right' }}>
          <Space>
            <Button
              icon={<SyncOutlined />}
              onClick={handleSyncUsers}
              loading={syncing}
              disabled={!selectedDevice}
            >
              Fetch Users from Device
            </Button>
            <Button
              icon={<LinkOutlined />}
              onClick={handleAutoMap}
              loading={autoMapping}
              disabled={!selectedDevice || mappings.length === 0}
            >
              Auto-Map by ID
            </Button>
            <Button
              icon={<ReloadOutlined />}
              onClick={fetchMappings}
              loading={loading}
            >
              Refresh
            </Button>
          </Space>
        </Col>
      </Row>

      {/* Stats */}
      {selectedDevice && mappings.length > 0 && (
        <Row gutter={16} style={{ marginBottom: 16 }}>
          <Col span={6}>
            <Card size="small">
              <Statistic
                title="Total Users"
                value={mappings.length}
                prefix={<UserOutlined />}
              />
            </Card>
          </Col>
          <Col span={6}>
            <Card size="small">
              <Statistic
                title="Mapped"
                value={mappedCount}
                valueStyle={{ color: '#52c41a' }}
                prefix={<CheckCircleOutlined />}
              />
            </Card>
          </Col>
          <Col span={6}>
            <Card size="small">
              <Statistic
                title="Unmapped"
                value={unmappedCount}
                valueStyle={{ color: unmappedCount > 0 ? '#faad14' : undefined }}
                prefix={<WarningOutlined />}
              />
            </Card>
          </Col>
          <Col span={6}>
            <Card size="small">
              <Statistic
                title="Pending Changes"
                value={pendingChanges.size}
                valueStyle={{ color: pendingChanges.size > 0 ? '#1890ff' : undefined }}
              />
            </Card>
          </Col>
        </Row>
      )}

      {/* Save Button */}
      {pendingChanges.size > 0 && (
        <Alert
          message={`You have ${pendingChanges.size} unsaved change(s)`}
          type="warning"
          showIcon
          style={{ marginBottom: 16 }}
          action={
            <Button type="primary" size="small" onClick={handleSaveChanges} loading={loading}>
              Save Changes
            </Button>
          }
        />
      )}

      {/* Unmapped Warning */}
      {unmappedCount > 0 && pendingChanges.size === 0 && (
        <Alert
          message={`${unmappedCount} user(s) are not mapped to employees`}
          description="Unmapped users' attendance will not be synced to HR records. Use 'Auto-Map by ID' or manually assign employees."
          type="info"
          showIcon
          style={{ marginBottom: 16 }}
        />
      )}

      {/* Table */}
      {selectedDevice ? (
        <Table
          columns={columns}
          dataSource={mappings}
          rowKey="id"
          loading={loading}
          pagination={{ pageSize: 20 }}
          size="middle"
          locale={{
            emptyText: (
              <Empty
                description="No users found"
                image={Empty.PRESENTED_IMAGE_SIMPLE}
              >
                <Button type="primary" onClick={handleSyncUsers} loading={syncing}>
                  Fetch Users from Device
                </Button>
              </Empty>
            )
          }}
        />
      ) : (
        <Empty description="Select a device to view employee mappings" />
      )}
    </div>
  );
};

export default ZKEmployeeMappingComponent;
